import { relative } from "path";
import React, { CSSProperties, ReactElement, useEffect, useState } from "react";
import { CompositionElement, getAllElementStats, getElementConstant, getElementStat } from "../../../shared/composition";
import { CellCategory } from "../../../shared/constants";
import { ElementDisplayInfo, KeywordDisplayInfo } from "../../../shared/elementDisplayInfo";
import { GameElement } from "../../../shared/gameElementTypes";
import { lineArtLocations } from '../../../shared/generated/assetLocations';
import { Colors } from "../palettes/colorPalette";
import { Tooltip } from "../styles/TooltipStyle";
import { BoldText, ColoredText } from "./keywordText";
import { getImmuneCellTier, getRewardCellTier } from "../../../shared/utils";
import { TierMarker } from "./TierMarker";
import { css } from "@emotion/react";

type CellDescriptionProps = {
	element: GameElement,
	compElement?: CompositionElement,
	showTooltips?: boolean,
	fixedHeight?: CSSProperties["height"],
	isReward?: boolean,
};

type StatsDisplayProps = {
	name: string,
	value: string,
	x: number,
	y: number,
	fill: string,
	reverse: string,
};

const StatsDisplay = (props: StatsDisplayProps) => {
	const pathId = `path_${props.name}`;
	return (
		<>
			<path id={pathId}>
				<animate attributeName="d" from={`M ${props.x} ${props.y} h0`} to={`M ${props.x} ${props.y} h1100`} dur="4s" begin="0s" repeatCount="1" fill="freeze" />
			</path>
			<text className="small" y={props.y} x={0} fill={props.fill} >
				<textPath xlinkHref={`#${pathId}`}>{props.name}: {props.value}</textPath>
			</text>
			{!props.reverse && <path className="path" d={`M ${props.x} ${props.y + 8} l 100 0 L 180 107`} fill="none" stroke={Colors.UITextAndBorders} pathLength="1" />}
			{props.reverse && <path className="path" d={`M ${props.x + 100} ${props.y + 11} l -100 0 L 180 107`} fill="none" stroke={Colors.UITextAndBorders} pathLength="1" />}
		</>
	)
};

StatsDisplay.defaultProps = {
	fill: Colors.UITextAndBorders,
}

enum DetailLevel {
	Normal,
	Detailed,
	Elite,
}

export const CellDescription = ({ isReward, element, compElement, showTooltips, fixedHeight }: CellDescriptionProps) => {
	const keywordToDisplay = (keyword: keyof typeof KeywordDisplayInfo) => {
		return keyword.replaceAll("_", " ");
	}

	let [detailLevel, setDetailLevel] = React.useState(DetailLevel.Normal);
	let [tier, setTier] = React.useState(0);

	const displayInfo = ElementDisplayInfo.get(element)!;

	useEffect(() => {
		const listener = (event: KeyboardEvent) => {
			if (event.key === " " && !isVirus) {
				const maxLevel = compElement?.promoted ? 2 : 3;
				let newLevel = (detailLevel + 1) % maxLevel;
				if (newLevel === DetailLevel.Detailed && !displayInfo.details) {
					newLevel = (newLevel + 1) % maxLevel;
				}
				setDetailLevel(newLevel);
			}
		}

		document.addEventListener('keydown', listener);
		return () => {
			document.removeEventListener('keydown', listener);
		};
	})

	useEffect(() => {
		setTier(isReward ? getRewardCellTier(element) : getImmuneCellTier(element));
		setDetailLevel(DetailLevel.Normal);
	}, [element, compElement, isReward]);

	const isVirus = element >= GameElement.VIRUS;

	let nextDetailText = ["detailed", "elite", "normal"][detailLevel];
	if (!displayInfo.details && !(compElement?.promoted)) {
		nextDetailText = ["elite", "normal", "normal"][detailLevel];;
	}
	const noAlternateDesc = (compElement?.promoted && !displayInfo.details) || isVirus;

	let isPromoted = detailLevel === DetailLevel.Elite || compElement?.promoted;

	let uniqueKeywords = new Set<keyof typeof KeywordDisplayInfo>();
	let keywords = new Array<keyof typeof KeywordDisplayInfo>();

	let description = displayInfo.description;

	//Get stats redo
	const displayStats: { [key: string]: { value: number, color: string } } = {};
	for (const stat in displayInfo.stats) {
		displayStats[stat] = { value: (displayInfo.stats as any)[stat], color: Colors.UIStat };
	}
	for (const constant in displayInfo.constants) {
		displayStats[constant] = { value: (displayInfo.constants as any)[constant], color: Colors.UIStat };
	}
	if (isPromoted) {
		for (const stat in displayInfo.veterenStats) {
			displayStats[stat] = { value: (displayInfo.veterenStats as any)[stat], color: Colors.UIIncreasedStat };
		}
		for (const stat in displayInfo.veterenConstants) {
			displayStats[stat] = { value: (displayInfo.veterenConstants as any)[stat], color: Colors.UIIncreasedStat };
		}
		if (!compElement) displayStats.health.value *= 3;
		else displayStats.health.value = (displayStats.health.value * 3) - (displayStats.health.value * compElement.combined)

		displayStats.health.color = Colors.UIIncreasedStat;
	}
	if (compElement) {
		for (const stat in compElement.changedStats) {
			let change = (compElement.changedStats as any)[stat];
			const base = displayStats[stat].value;
			const value = base + change;
			let weight = value === base ? 0 : Math.sign(value - base) as 1 | 0 | -1;
			if (stat === "cooldown" && weight === -1) weight = 1; //this stat is better when smaller
			let color = weight === 1 ? Colors.UIIncreasedStat : weight === -1 ? Colors.UIDecreasedStat : Colors.UIHighlight;
			if (compElement.promoted && value === base * 3) color = Colors.UIStat;
			displayStats[stat] = { value, color };
		}
	}

	if (isPromoted && displayInfo.veterenDescription) {
		description = displayInfo.veterenDescription;
	}

	if (isPromoted) {
		keywords.push("elite");
	}

	//Main description
	const regex = /\$[\w-]+/g;
	const matches = description.matchAll(regex);
	let descriptionElements = [];
	let descriptionRemaining = description;
	let lengthProcessed = 0;
	if (matches) {
		for (const match of matches) {
			const matchStr = match.toString()
			let key = matchStr.substring(1, matchStr.length);
			const lowercase = key.toLowerCase() as keyof typeof KeywordDisplayInfo;
			let replacement: ReactElement;
			if (lowercase in KeywordDisplayInfo) {
				replacement = <span key={key + lengthProcessed} style={{ textTransform: "capitalize" }}>{BoldText(keywordToDisplay(lowercase))}</span>;
				if (!uniqueKeywords.has(lowercase)) {
					uniqueKeywords.add(lowercase);
					keywords.push(lowercase);
				}
			}
			else {
				const foundStat = displayStats[key];
				const value = foundStat && foundStat.value ? foundStat.value.toString() : key
				const color = foundStat ? foundStat.color : Colors.UIStat;
				replacement = ColoredText(color, value, undefined, key + lengthProcessed);
				//TODO: add styling
			}

			const start = (match.index || 0) - lengthProcessed;
			const end = start + matchStr.length;
			const before = descriptionRemaining.substring(0, start);
			descriptionRemaining = descriptionRemaining.substring(end);
			descriptionElements.push(<span key={before + lengthProcessed}>{before}</span>);
			descriptionElements.push(replacement);
			lengthProcessed += end;
		}
	}
	descriptionElements.push(<span key="rest_of_desc">{descriptionRemaining}</span>);

	//Smaller detailed description
	let detailElements = new Array<JSX.Element>();
	const details = displayInfo.details
	if (details) {
		const matches = details.matchAll(regex);
		let detailRemaining = details;
		lengthProcessed = 0;
		if (matches) {
			for (const match of matches) {
				const matchStr = match.toString()
				let key = matchStr.substring(1, matchStr.length);
				const lowercase = key.toLowerCase() as keyof typeof KeywordDisplayInfo;
				let replacement: ReactElement;
				if (lowercase in KeywordDisplayInfo) {
					replacement = <span key={key + lengthProcessed} style={{ textTransform: "capitalize" }}>{BoldText(keywordToDisplay(lowercase))}</span>;
					if (!uniqueKeywords.has(lowercase)) {
						uniqueKeywords.add(lowercase);
						keywords.push(lowercase);
					}
				}
				else {
					const foundStat = displayStats[key];
					const value = foundStat && foundStat.value ? foundStat.value.toString() : key
					const color = foundStat ? foundStat.color : Colors.UIStat;
					replacement = ColoredText(color, value, undefined, key + lengthProcessed);
					//TODO: add styling
				}

				const start = (match.index || 0) - lengthProcessed;
				const end = start + matchStr.length;
				const before = detailRemaining.substring(0, start);
				detailRemaining = detailRemaining.substring(end);
				detailElements.push(<span key={before + lengthProcessed}>{before}</span>);
				detailElements.push(replacement);
				lengthProcessed += end;
			}
		}
		detailElements.push(<span key="rest_of_desc">{detailRemaining}</span>);
	}

	const category = displayInfo.category;
	let categoryName = "";
	switch (category) {
		case CellCategory.BCell:
			categoryName = "B-Cell";
			if (!uniqueKeywords.has("b-cell")) {
				keywords.push("b-cell");
			}
			break;
		case CellCategory.TCell:
			categoryName = "T-Cell";
			if (!uniqueKeywords.has("t-cell")) {
				keywords.push("t-cell");
			}
			break;
	}

	const assetLocation = (lineArtLocations as any)[displayInfo.asset];
	if (!assetLocation) {
		throw new Error("Missing asset in line art " + displayInfo.asset);
	}

	return (
		<div style={{ display: "flex" }}>
			<Tooltip id="cellDescription" style={{ height: fixedHeight ? fixedHeight : undefined }}>
				{element < GameElement.VIRUS &&
					<div style={{ display: "flex", alignItems: "center", justifyContent: "right", position: "relative", "left": "1.5em", top: "0.5em" }}>
						<span>Tier </span>
						<TierMarker tier={tier} displayNumber={true} relative={true} isReward={isReward}/>
					</div>
				}
				<h2 style={{
					fontFamily: "'NotoSansDisplay', sans-serif",
					marginBlockEnd: "0",
					gridArea: "title"
				}}>{isPromoted ? "Elite " : ""}{displayInfo.displayName}</h2>
				{categoryName !== "" && <i><b>{categoryName}</b></i>}
				<svg viewBox="0 20 365 140" style={{}}>
					<style>
						{`
				.small { font-size: .8rem; font-family: 'NotoSerif', serif; font-weight: 600 }
				.path {
					stroke-dasharray: 1;
					stroke-dashoffset: 1;
					animation: dash 0.3s ease-in;
					animation-fill-mode: forwards;
				}

				@keyframes dash {
					from {
						stroke-dashoffset: 1;
					}
					to {
						stroke-dashoffset: 0;
					}
				}
			`}
					</style>

					<defs>
						<clipPath id="clip">
							<rect x="0" y="0" width="64" height="64"></rect>
						</clipPath>
					</defs>

					{displayStats.health && <StatsDisplay y={64} x={20} fill={displayStats.health.color} name="Health" value={displayStats.health.value.toString()} reverse="" />}
					{displayStats.range && <StatsDisplay y={97} x={20} fill={displayStats.range.color} name="Range" value={displayStats.range.value.toString()} reverse="" />}
					{displayStats.speed && <StatsDisplay y={130} x={20} fill={displayStats.speed.color} name="Speed" value={displayStats.speed.value + " m/s"} reverse="" />}
					{displayStats.cooldown && <StatsDisplay y={130} x={250} fill={displayStats.cooldown.color} name="Cooldown" value={displayStats.cooldown.value.toString()} reverse="true" />}
					{displayStats.effectRadius && <StatsDisplay y={64} x={250} fill={displayStats.effectRadius.color} name="Reach" value={displayStats.effectRadius.value.toString() + ' m'} reverse="true" />}
					<svg x="142" y="75" width="128" height="128" clipPath="url(#clip)">
						<image x={assetLocation.x} y={-assetLocation.y} href="assets/lineArtSpriteSheet.png" style={{
							transform: "scale(0.5)"
						}} />
					</svg>
				</svg>
				{/* <div style={{
					backgroundImage: `url(assets/lineArtSpriteSheet.png )`,
					backgroundPosition: `${assetLocation.x / 2}px ${-assetLocation.y / 2}px`,
					backgroundSize: `64px`,
					display: "inline-block",
					width: `${assetLocation.width / 2}px`,
					height: `${assetLocation.height / 2}px`,
					zIndex: 1,
					position: "absolute",
					left: "calc(50% - 32px)",
					top: "calc(50% - 32px)",
				}}></div> */}
				<span style={{
					fontFamily: "'NotoSans', sans-serif",
					marginBlockStart: "0",
					marginBottom: "1em",
				}}>
					{...descriptionElements}
					{detailElements.length > 0 && detailLevel > DetailLevel.Normal && <div style={{ fontWeight: "lighter", marginTop: "0.8em", color: Colors.UIUnemphasizedText }}>
						({...detailElements})
					</div>}
				</span>
				{keywords.length > 0 && <p style={{
					fontFamily: "'NotoSans', sans-serif",
					marginBlockStart: "0",
					fontSize: "0.8rem",
					fontWeight: "bold",
				}}>
				</p>}
				{!noAlternateDesc && <div style={{ color: "#666699", fontSize: "smaller", fontWeight: 500, fontStyle: "italic" }}>Press SPACE to show {nextDetailText} description</div>}
			</Tooltip>
			{showTooltips && <div style={{ position: "absolute", left: "25vw", pointerEvents: "none" }}>
				{keywords.map(((keyword, idx) =>
					<Tooltip key={keyword} style={{
						textAlign: "left",
						padding: "10px",
						fontSize: "20px",
					}}><span key={keyword} style={{ textTransform: "capitalize" }}>{BoldText(keywordToDisplay(keyword))}</span>{KeywordDisplayInfo[keyword]}
					</Tooltip>))}
			</div>}
		</div >
	)
}
